import React from "react";
import api from "../services/ApiService";
import { Container, Text, Group, Button, Loader } from "@mantine/core";
import { useNavigate } from "react-router";
import MSIcon from "components/MSIcon.jsx";
import GridContainer from "components/GridContainer.jsx";
import ApartmentCard from "components/ApartmentCard.jsx";
import ApartmentFilter from "./Apartment/ApartmentFilter.jsx";
import getLocalizedField from "services/getLocalizedField.js";
import { useTrans } from "core/index.js";
import { Api_Apartment, Api_Features, Api_GetPublished, Api_Region } from "../../../constants/SubApi.js";

export default function Home() {
	const [loading, setLoading] = React.useState(true);
	const [apartments, setApartments] = React.useState([]);
	const [regions, setRegions] = React.useState([]);
	const [features, setFeatures] = React.useState([]);
	const { t } = useTrans();
	const [priceBounds, setPriceBounds] = React.useState({ min: 0, max: 1000000 });
	const [showFilter, setShowFilter] = React.useState(false);

	const navigate = useNavigate();

	React.useEffect(() => {
		loadInitial();
	}, []);

	async function loadInitial() {
		try {
			setLoading(true);
			const arch = JSON.parse(localStorage.getItem("architecture") || "{}");
			if (arch.range) {
				setPriceBounds({ min: arch.range.min, max: arch.range.max });
			}

			const [aptRes, regRes, featRes] = await Promise.all([
				api.get(`/${Api_Apartment}/${Api_GetPublished}?offset=0&limit=50`),
				api.get(`/${Api_Region}/all`),
				api.get(`/${Api_Features}/all`),
			]);

			setApartments(aptRes.data || []);
			setRegions(regRes || []);
			setFeatures(featRes || []);
		} finally {
			setLoading(false);
		}
	}

	async function applyFilters(filters) {
		if (!filters) {
			await loadInitial();
			return;
		}

		const payload = {
			minPrice: filters.priceRange[0],
			maxPrice: filters.priceRange[1],
			regionId: filters.regionId ? Number(filters.regionId) : null,
			evaluate: filters.rate,
			startDate: filters.startDate ? new Date(filters.startDate).getTime() : null,
			endDate: filters.endDate ? new Date(filters.endDate).getTime() : null,
			features: buildFeatureArray(filters.features),
		};

		const cleanFilter = buildFilter(payload);
		const filterString = JSON.stringify(cleanFilter);

		try {
			setLoading(true);
			const url = `/${Api_Apartment}/${Api_GetPublished}?offset=0&limit=50&filter=${encodeURIComponent(filterString)}`;
			const res = await api.get(url);
			setApartments(res?.data || []);
		} catch (err) {
			console.error(err);
		} finally {
			setLoading(false);
		}
	}

	function buildFeatureArray(features) {
		if (!Array.isArray(features)) return [];

		return features.filter((f) => {
			if (!f) return false;
			if (f.value === null || f.value === undefined) return false;
			if (typeof f.value === "boolean") return true;
			if (typeof f.value === "object") {
				if (f.value.minValue == null) return false;
				if (f.value.maxValue == null) return false;
				return true;
			}
			return false;
		});
	}

	if (loading)
		return (
			<div style={{ padding: 32, textAlign: "center" }}>
				<Loader size="xl" />
			</div>
		);

	return (
		<Container size="xl" style={{ padding: 12 }}>
			<Group justify="space-between" mb="lg">
				<Text size="xl" fw={700}>
					{t("apartment.availableApartments")}
				</Text>

				<Button
					leftSection={<MSIcon name="FilterCheckOutline" />}
					variant="outline"
					onClick={() => setShowFilter(true)}
				>
					{t("apartment.filters")}
				</Button>
			</Group>

			{apartments.length === 0 ? (
				<>
					<Text ta="center" c="dimmed">
						{t("messages.noDataFound")}
					</Text>
					<Button fullWidth mt="md" onClick={() => loadInitial()}>
						{t("apartment.clearFilters")}
					</Button>
				</>
			) : (
				<GridContainer>
					{apartments.map((apt) => (
						<ApartmentCard
							key={apt.id}
							title={getLocalizedField(apt, "name")}
							desc={getLocalizedField(apt, "desc")}
							price={apt.dailyPrice}
							currency={getLocalizedField(apt, "currency_name")}
							image={apt.baseImage}
							rating={apt.evaluate}
							onClick={() => navigate(`/apartments/${apt.id}`)}
						/>
					))}
				</GridContainer>
			)}

			<ApartmentFilter
				opened={showFilter}
				onClose={() => setShowFilter(false)}
				onApply={applyFilters}
				initialRange={priceBounds}
				regions={regions}
				features={features}
			/>
		</Container>
	);
}

function buildFilter(clean) {
	const f = { ...clean };

	if (f.minPrice == null) delete f.minPrice;
	if (f.maxPrice == null) delete f.maxPrice;
	if (!f.regionId) delete f.regionId;
	if (!f.evaluate) delete f.evaluate;
	if (!f.startDate) delete f.startDate;
	if (!f.endDate) delete f.endDate;

	if (Array.isArray(f.features)) {
		f.features = f.features.filter((x) => {
			if (!x) return false;
			if (x.value === null || x.value === undefined) return false;
			if (typeof x.value === "boolean") return true;
			if (typeof x.value === "object") {
				if (x.value.minValue == null) return false;
				if (x.value.maxValue == null) return false;
				return true;
			}
			return false;
		});

		if (f.features.length === 0) delete f.features;
	}

	return f;
}
